import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Building2, Check, FolderKanban, Plus, Users } from '@/components/icons';
import { ROLE_LABELS } from '@1qa/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import { Badge, Button, Card, Dialog, EmptyState, ErrorNotice, Field, Input, PageHeader, PageLoader, useToast } from '@/components/ui';

export function OrganizationsPage() {
  const { user, canPlatform, roleIn, currentOrgId, setCurrentOrgId, refresh } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const orgs = useQuery({ queryKey: ['orgs'], queryFn: api.orgs });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const create = useMutation({
    mutationFn: () => api.createOrg(name.trim()),
    onSuccess: async (org) => {
      toast.success(`Organization “${org.name}” created`);
      setOpen(false);
      setName('');
      await Promise.all([qc.invalidateQueries({ queryKey: ['orgs'] }), refresh()]);
      setCurrentOrgId(org.id);
      navigate('/members');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate();
  };

  // A Project Admin creates one organization; the platform Admin only looks.
  const ownsOrg = (orgs.data ?? []).some((o) => o.createdById === user?.id);
  const canCreate = canPlatform('org.create') && !ownsOrg;

  return (
    <div>
      <PageHeader
        title="Organizations"
        description="Organizations group your team, test projects and reports. A Project Admin creates one organization."
        actions={
          canCreate && (
            <Button onClick={() => setOpen(true)} icon={<Plus className="size-4" />}>
              New organization
            </Button>
          )
        }
      />
      {orgs.isPending ? (
        <PageLoader />
      ) : orgs.isError ? (
        <ErrorNotice error={orgs.error} onRetry={() => orgs.refetch()} />
      ) : orgs.data.length === 0 ? (
        <EmptyState
          icon={<Building2 className="size-6" />}
          title="No organizations yet"
          description={
            canCreate ? 'Create your organization, then invite your team.' : 'Ask a Project Admin to add you to their organization.'
          }
          action={canCreate && <Button onClick={() => setOpen(true)}>Create organization</Button>}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {orgs.data.map((org) => {
            const role = roleIn(org.id);
            const active = org.id === currentOrgId;
            return (
              <Card key={org.id} interactive className="flex flex-col p-5">
                <div className="flex items-start gap-3">
                  <span className="grid size-11 place-items-center rounded-xl bg-brand-gradient text-lg font-bold text-white">
                    {org.name.charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-semibold">{org.name}</h3>
                    <p className="text-xs text-slate-500">Created {timeAgo(org.createdAt)}</p>
                  </div>
                  {role && <Badge tone="brand">{ROLE_LABELS[role]}</Badge>}
                </div>
                {org.createdBy && (
                  <p className="mt-4 truncate text-sm text-slate-600 dark:text-slate-300">
                    Owner: <span className="font-medium">{org.createdBy.name}</span> <span className="text-slate-400">· {org.createdBy.email}</span>
                  </p>
                )}
                {org._count && (
                  <div className="mt-2 flex gap-4 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                      <Users className="size-3.5" /> {org._count.memberships} member{org._count.memberships === 1 ? '' : 's'}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <FolderKanban className="size-3.5" /> {org._count.projects} project{org._count.projects === 1 ? '' : 's'}
                    </span>
                  </div>
                )}
                <div className="mt-auto flex justify-end pt-5">
                  {active ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 dark:text-emerald-400">
                      <Check className="size-4" /> Current
                    </span>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => setCurrentOrgId(org.id)}>
                      Switch to <ArrowRight className="size-4" />
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="New organization"
        description="You'll become its Project Admin and can invite members right away."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="new-org" loading={create.isPending} disabled={!name.trim()}>
              Create
            </Button>
          </>
        }
      >
        <form id="new-org" onSubmit={onSubmit}>
          <Field label="Organization name">
            {(id) => <Input id={id} autoFocus placeholder="Acme Inc." value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />}
          </Field>
        </form>
      </Dialog>
    </div>
  );
}
